import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { getBrandById } from './useBrands';
import { getAllMarkets } from './useMarkets';
import type { MarketRecord } from '../types';

type UseEditProductMarketsParams = {
  organizationId?: number | string | null;
  productId?: number | string | null;
  productIdentity?: unknown;
  brandId?: number | string | null;
  selectedMarketIds?: Array<number | string>;
};

type EditProductMarketsState = {
  markets: MarketRecord[];
  loading: boolean;
  error: string;
  isReady: boolean;
};

const initialState: EditProductMarketsState = {
  markets: [],
  loading: false,
  error: '',
  isReady: false,
};

export default function useEditProductMarkets({
  organizationId,
  productId,
  productIdentity,
  brandId,
  selectedMarketIds = [],
}: UseEditProductMarketsParams) {
  const requestIdRef = useRef(0);
  const selectionEditedRef = useRef(false);
  const selectionContextRef = useRef({ organizationId, productId, productIdentity });
  const [retryVersion, setRetryVersion] = useState(0);
  const [state, setState] = useState<EditProductMarketsState>(initialState);
  const selectedMarketIdsKey = selectedMarketIds.map(String).sort().join(',');

  const retry = useCallback(() => {
    setRetryVersion((version) => version + 1);
  }, []);

  const selectMarkets = useCallback((markets: MarketRecord[]) => {
    selectionEditedRef.current = true;
    setState((current) => ({ ...current, markets }));
  }, []);

  useLayoutEffect(() => {
    const requestId = ++requestIdRef.current;
    const previousContext = selectionContextRef.current;
    const productChanged = previousContext.organizationId !== organizationId
      || previousContext.productId !== productId
      || previousContext.productIdentity !== productIdentity;
    if (productChanged) {
      selectionEditedRef.current = false;
      selectionContextRef.current = { organizationId, productId, productIdentity };
    }

    if (!organizationId || !productId || !brandId) {
      setState(initialState);
      return () => {
        requestIdRef.current += 1;
      };
    }

    setState((current) => ({
      markets: productChanged ? [] : current.markets,
      loading: true,
      error: '',
      isReady: false,
    }));

    async function loadMarkets() {
      try {
        const [brand, allMarkets] = await Promise.all([
          getBrandById(organizationId, brandId),
          getAllMarkets(organizationId),
        ]);
        if (requestId !== requestIdRef.current) return;

        const brandMarketIds = new Set((brand?.marketIds || []).map(String));
        setState((current) => {
          const selectedIds = new Set((selectionEditedRef.current
            ? current.markets.filter((market) => market.selected).map((market) => market.id)
            : selectedMarketIds).map(String));
          const markets = allMarkets
            .filter((market) => brandMarketIds.has(String(market.id)))
            .map((market) => ({ ...market, selected: selectedIds.has(String(market.id)) }));
          return { markets, loading: false, error: '', isReady: true };
        });
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        console.error(error);
        setState((current) => ({
          ...current,
          loading: false,
          error: 'Markets could not be loaded. Please try again.',
          isReady: false,
        }));
      }
    }

    loadMarkets();

    return () => {
      requestIdRef.current += 1;
    };
  }, [brandId, organizationId, productId, productIdentity, retryVersion, selectedMarketIdsKey]);

  return { ...state, retry, selectMarkets };
}
